import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Mail, AlertTriangle } from "lucide-react";
import { AdminTitle } from "./AdminLayout";
import { http, apiError, formatDateTime } from "../../lib/api";
import { INQ_STATUS } from "./inquiryUi";

const DIVS = [["truck", "Truck"], ["trailer", "Trailer"], ["marine", "Marine"], ["equipment", "Equipment"]];

const Delta = ({ cur, prev }) => {
  if (prev === null || prev === undefined) return <span className="text-slate-400">no prior data</span>;
  const d = cur - prev;
  return <span className={d > 0 ? "text-emerald-300" : d < 0 ? "text-rose-300" : "text-slate-400"}>{d > 0 ? "+" : ""}{d} vs {prev}</span>;
};

const Stat = ({ label, value, cmp, id }) => (
  <div className="bg-[var(--ink)] p-6" data-testid={`weekly-stat-${id}`}>
    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p className="font-display text-4xl mt-2 text-slate-50" data-testid={`weekly-stat-${id}-value`}>{value ?? 0}</p>
    <p className="text-xs mt-2">{cmp}</p>
  </div>
);

const Block = ({ title, children, testid }) => (
  <section className="border hairline bg-[var(--ink-2)] p-6 min-w-0" data-testid={testid}>
    <p className="eyebrow mb-4">{title}</p>
    {children}
  </section>
);

const Members = ({ members }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm min-w-[420px]">
      <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400">{["Team member", "Assigned", "Completed", "Open", "Overdue"].map((h) => <th key={h} className="pb-2 pr-4 font-normal">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-white/10">
        {members.map((m) => (
          <tr key={m.id} data-testid={`weekly-member-${m.role}`}>
            <td className="py-3 pr-4"><span className="block text-slate-100">{m.name}</span><span className="text-xs text-slate-400 capitalize">{m.role}</span></td>
            <td className="pr-4">{m.assigned}</td><td className="pr-4">{m.completed}</td><td className="pr-4">{m.open}</td>
            <td className={`pr-4 ${m.overdue ? "text-rose-300" : ""}`}>{m.overdue}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Attention = ({ items }) => (items.length ? (
  <ul className="divide-y divide-white/10">
    {items.map((a) => (
      <li key={a.id} className="py-3">
        <Link to={`/admin/inquiries?id=${a.id}`} className="text-sm text-slate-100 hover:text-gold" data-testid={`weekly-attention-${a.id}`}>{a.name}</Link>
        <span className="text-xs text-slate-400"> · {a.ref} · {a.assigned_name || "Unassigned"}</span>
        <p className="text-xs text-rose-300 mt-1 flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{a.reasons.join("; ")}</p>
      </li>
    ))}
  </ul>
) : <p className="text-sm text-slate-400">Nothing needs your attention right now.</p>);

export default function WeeklyReport() {
  const [busy, setBusy] = useState(false);
  const { data: r, isLoading, error } = useQuery({ queryKey: ["weekly-report"], queryFn: async () => (await http.get("/admin/reports/weekly")).data });
  const send = async () => {
    setBusy(true);
    try {
      await http.post("/admin/reports/weekly/send-me");
      toast.success("Weekly report emailed to you");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  if (isLoading) return <Loader2 className="w-6 h-6 animate-spin text-gold" />;
  if (error || !r) return <p className="text-rose-300" data-testid="weekly-error">{apiError(error)}</p>;
  const t = r.totals || {};
  const p = r.previous;
  const snap = r.previous_snapshot || {};
  return (
    <div className="space-y-8" data-testid="weekly-report">
      <AdminTitle title="Weekly Performance" sub={`Week of ${r.label} (Central Time). Emailed to you every Monday at 8:00 AM.`}>
        <button className="btn-gold" onClick={send} disabled={busy} data-testid="weekly-send-me">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email me this report</button>
      </AdminTitle>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-px bg-white/10 border hairline">
        <Stat id="received" label="Inquiries received" value={t.received} cmp={<Delta cur={t.received} prev={p?.received} />} />
        <Stat id="completed" label="Inquiries completed" value={t.completed} cmp={<Delta cur={t.completed} prev={p?.completed} />} />
        <Stat id="open" label="Still open (now)" value={t.open} cmp={<Delta cur={t.open} prev={snap.open} />} />
        <Stat id="overdue" label="Overdue follow-ups (now)" value={t.overdue_followups} cmp={<Delta cur={t.overdue_followups} prev={snap.overdue_followups} />} />
        <Stat id="followups-done" label="Follow-ups completed" value={t.followups_completed} cmp={<Delta cur={t.followups_completed} prev={r.previous_followups_tracked ? p?.followups_completed : null} />} />
        <Stat id="unassigned" label="Unassigned open" value={t.unassigned_open} cmp={<Delta cur={t.unassigned_open} prev={snap.unassigned_open} />} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Block title="Team performance" testid="weekly-members"><Members members={r.members || []} /></Block>
        <Block title={`Needs owner attention · ${(r.attention || []).length}`} testid="weekly-attention"><Attention items={r.attention || []} /></Block>
        <Block title="Received by division" testid="weekly-divisions">
          <dl className="divide-y divide-white/10 text-sm">
            {DIVS.map(([k, label]) => (
              <div key={k} className="flex justify-between py-2" data-testid={`weekly-division-${k}`}><dt className="text-slate-300">{label}</dt><dd>{t.by_division?.[k] ?? 0} <span className="text-xs ml-2"><Delta cur={t.by_division?.[k] ?? 0} prev={p?.by_division?.[k]} /></span></dd></div>
            ))}
            {t.by_division?.general > 0 && <div className="flex justify-between py-2"><dt className="text-slate-300">General / unspecified</dt><dd>{t.by_division.general}</dd></div>}
          </dl>
        </Block>
        <Block title="Status breakdown (all inquiries, now)" testid="weekly-statuses">
          <dl className="divide-y divide-white/10 text-sm">
            {Object.entries(r.status_counts || {}).map(([k, v]) => <div key={k} className="flex justify-between py-2"><dt className="text-slate-300">{INQ_STATUS[k]?.label || k}</dt><dd>{v}</dd></div>)}
          </dl>
        </Block>
      </div>
      <div className="text-xs text-slate-400 space-y-1" data-testid="weekly-notes">
        <p>Received, assigned, completed and follow-ups completed count activity during the week. Open and overdue figures are as of {formatDateTime(r.generated_at)}.</p>
        {r.followup_tracking_since && <p>Follow-up completion tracking began {formatDateTime(r.followup_tracking_since)}.</p>}
        {!p && <p>Week-over-week comparison appears once there is a full prior week of inquiry records.</p>}
        {(r.data_notes || []).map((n) => <p key={n}>{n}</p>)}
      </div>
    </div>
  );
}
